<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { eventBus } from '@/utils/core/eventBus';

const iframeRef = ref(null);
const isIpadPortrait = ref(deviceUtils.isIpadPortrait());

const applyHeight = () => {
	if (!iframeRef.value) return;
	applyIpadClasses();
};

const applyIpadClasses = () => {
	const portrait = deviceUtils.isIpadPortrait();
	isIpadPortrait.value = portrait;
	const body = iframeRef.value?.contentDocument?.body;
	if (body) {
		body.classList.toggle('ipad-portrait', portrait);
		body.classList.toggle('ipad-landscape', deviceUtils.isIpadLandscape());
	}
};

// Debounced to avoid capturing mid-animation heights (e.g. orientation change)
let resizeTimer;
const resizeIframe = () => {
	clearTimeout(resizeTimer);
	resizeTimer = setTimeout(() => {
		applyHeight();
		applyIpadClasses();
	}, 150);
};

const loadApp = (url, token, form) => {
	form.action = url;
	form.token.value = token;
	form.submit();
};

// Named so it can be removed on unmount — an anonymous listener here leaked on every
// remount/HMR of the iframe host (and each surviving copy re-submits a form on loadApp).
const handleParentMessage = (event) => {
	if (event.data?.type === 'loadApp') {
		const { url, token } = event.data;

		const form = document.createElement('form');
		form.method = 'POST';
		form.action = url;
		form.style.display = 'none';

		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = 'token';
		input.value = token;

		form.appendChild(input);
		document.body.appendChild(form);
		form.submit();
	}
};

onMounted(() => {
	window.addEventListener('resize', resizeIframe);
	iframeRef.value?.addEventListener('load', applyIpadClasses);

	eventBus.on('view-changed', applyHeight);

	window.addEventListener('message', handleParentMessage);

	nextTick(applyHeight);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', resizeIframe);
	iframeRef.value?.removeEventListener('load', applyIpadClasses);
	clearTimeout(resizeTimer);
	eventBus.off('view-changed', applyHeight);
	window.removeEventListener('message', handleParentMessage);
});
</script>

<template>
	<div class="layout" :class="{ 'ipad-portrait': isIpadPortrait }">
		<iframe ref="iframeRef" title="Tycoon Mobile" src="/m.eagle/default.htm" width="100%" style="border: none" allow="geolocation *; fullscreen *; clipboard-write; display-capture"></iframe>
	</div>
</template>

<style scoped>
.layout {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100dvh;
	padding-top: env(safe-area-inset-top);
	box-sizing: border-box;
}

iframe {
	flex: 1;
	min-height: 0;
	width: 100%;
	border: none;
	transform: scale(1);
	transform-origin: top left;
}
</style>
